import { format, parse } from 'date-fns';

import { ENCRYPTED_CARD_ERROR_CODE, SENSITIVE_MASK_CHAR } from './constants.ts';

/**
 * Fields required to encrypt credit card data.
 */
export type CreditCardFields = {
  creditCard: string;
  cvv?: string;
};

/**
 * Credit card data that is encrypted.
 */
export type CreditCardFieldsEncrypted = {
  creditCardEncrypted: string | null;
  cvvEncrypted: string | null;
};

/**
 * Checks if server-encrypted card had an error.
 * @param number - Encrypted card number.
 * @returns Whether the card had an error.
 */
export const encryptedCardHasError = (number: string) =>
  !number || number.includes(ENCRYPTED_CARD_ERROR_CODE);

/**
 * Checks if frontend has masked a server-encrypted card number.
 */
export const isEncryptedCardMask = (number: string) =>
  number?.includes(SENSITIVE_MASK_CHAR);

/**
 * Encrypts credit card data.
 * @param creditCardFields - Fields required to encrypt credit card data.
 * @param useCvv - Whether to use the CVV.
 * @param TxEncrypt - Function to encrypt the data. Generally this will come from TokenEx.
 * @param key - Key to use to encrypt the data.
 * @returns Encrypted credit card data.
 */
export const encryptCreditCardData = (
  creditCardFields: CreditCardFields,
  useCvv: boolean,
  TxEncrypt: (key: string, s: string) => string,
  key: string,
): CreditCardFieldsEncrypted => {
  // grab the PAN
  const { creditCard, cvv } = creditCardFields;
  if (!creditCard) {
    return {
      cvvEncrypted: null,
      creditCardEncrypted: null,
    };
  }

  const cardNumber = creditCard.replaceAll(/[^0-9]/g, '');

  // encrypt the data
  let cardCipherText;
  let cvvCipherText;
  try {
    cardCipherText = TxEncrypt(key, cardNumber);
    cvvCipherText = useCvv && cvv ? TxEncrypt(key, cvv) : '';
  } catch {
    cardCipherText = ENCRYPTED_CARD_ERROR_CODE;
    cvvCipherText = null;
  }

  return {
    creditCardEncrypted: cardCipherText,
    cvvEncrypted: cvvCipherText,
  };
};

/**
 * Formats the credit card expiry date to M/yy.
 * @param expirationDate - The expiration date in MM/yyyy format.
 * @returns The shortened expiration date.
 */
export const getShortExpiryDate = (expirationDate: string) => {
  const expiryDate = expirationDate
    ? parse(expirationDate, 'MM/yyyy', new Date())
    : null;

  if (expiryDate) {
    return format(expiryDate!, 'M/yy');
  }

  return '';
};

/**
 * Formats the credit card expiry date to MM/yyyy.
 * @param expirationDate - The expiration date in M/yy format.
 * @returns The long expiration date.
 */
export const getLongExpiryDate = (expirationDate: string) => {
  const expiryDate = expirationDate
    ? parse(expirationDate, 'M/yy', new Date())
    : null;

  if (expiryDate) {
    return format(expiryDate!, 'MM/yyyy');
  }

  return '';
};
